import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    useFetchUserDetails,
    useFetchUserPractices,
} from "../../modules/auth/hooks/auth.queries";
import { fetchUserPractices } from "../../modules/auth/services/auth.services";
import axiosInstance from "../../utils/axios";
import { getClientDisplayName, getClientInitials } from "../../utils/helpers";
import Avatar from "../Avatar/Avatar";
import PracticeSelection from "../PracticeSelection/PracticeSelection";
import { handleChangePractice } from "../../redux/features/practice/practiceSlice";
import { useAppDispatch } from "../../hooks/useRedux";
import useToast from "../../hooks/useToast";
import {
    getClientSession,
    setClientSession,
} from "../../utils/clientSessionStore";

export default function SwitchProfile() {
    const [userId, setUserId] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const loggedInUser = useFetchUserDetails();
    const { data: userPractices, isLoading: isPracticesLoading } =
        useFetchUserPractices(userId || "");
    const dispatch = useAppDispatch();
    const { toast } = useToast();

    const handleSelectProfile = async (id: string) => {
        setUserId(id);
        localStorage.setItem("userId", id);
        queryClient.invalidateQueries({ queryKey: ["user-practices"] });

        const requestId = id;

        try {
            const practicesData = await fetchUserPractices(id);

            const { userId: activeUserId } = getClientSession();

            if (activeUserId && activeUserId !== requestId) {
                return;
            }

            if (!practicesData?.data?.practices) {
                toast({
                    mode: "error",
                    message: "Failed to load practices. Please try again.",
                });

                setClientSession({
                    userId: null,
                });

                setUserId(null);
                return;
            }

            if (practicesData.data.practices.length === 0) {
                toast({
                    mode: "error",
                    message: "This profile has no associated practices.",
                });

                setClientSession({
                    userId: null,
                });

                setUserId(null);
                return;
            }

            if (practicesData.data.practices.length === 1) {
                const selectedPractice = practicesData.data.practices[0];
                axiosInstance.defaults.headers.common.practice_id =
                    selectedPractice.practice_id;
                axiosInstance.defaults.headers.common.client_id =
                    selectedPractice.client_id;

                setClientSession({
                    clientId: selectedPractice.client_id,
                    userId: id,
                    practice: selectedPractice,
                });

                dispatch(
                    handleChangePractice({
                        practice: selectedPractice,
                    })
                );
                window.location.href = `/`;
            }
        } catch (error) {
            const { userId: activeUserId } = getClientSession();
            if (activeUserId && activeUserId !== requestId) {
                return;
            }

            toast({
                mode: "error",
                message: "Failed to load practices. Please try again.",
            });

            setClientSession({
                userId: null,
            });

            setUserId(null);
        }
    };

    if (userId && !isPracticesLoading && userPractices?.data?.practices) {
        if (userPractices.data.practices.length > 1) {
            return <PracticeSelection />;
        }
    }

    return (
        <div className="flex flex-col gap-y-4 items-center h-dvh justify-center">
            <p className="text-xl mb-16 font-medium text-primary">
                Please select your profile to continue
            </p>

            {loggedInUser.data ? (
                <div className="flex gap-x-12 items-center">
                    <div className="flex flex-col items-center gap-y-4">
                        <Avatar
                            label={getClientInitials(
                                loggedInUser?.data?.data.preferred_name,
                                loggedInUser?.data?.data.first_name,
                                loggedInUser?.data?.data.last_name
                            )}
                            className="w-[80px] h-[80px] text-base"
                            onClick={() =>
                                handleSelectProfile(
                                    loggedInUser.data.data.user_id
                                )
                            }
                        />
                        <p className="text-base font-medium">
                            {getClientDisplayName(
                                loggedInUser?.data.data.preferred_name,
                                loggedInUser?.data.data.first_name
                            )}
                        </p>
                    </div>
                    {loggedInUser.data.data.sub_profiles.map((profile) => (
                        <div
                            key={profile.user_id}
                            className="flex flex-col items-center gap-y-4"
                        >
                            <Avatar
                                label={getClientInitials(
                                    profile.preferred_name,
                                    profile.first_name,
                                    profile.last_name
                                )}
                                className="w-[80px] h-[80px] text-base"
                                onClick={() =>
                                    handleSelectProfile(profile.user_id)
                                }
                            />
                            <p className="text-base font-medium">
                                {getClientDisplayName(
                                    profile.preferred_name,
                                    profile.first_name
                                )}
                            </p>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
